import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {PaletteColorsEnum} from '../../../libs/shared/ui/src/styles/paletteModel';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {IconBox} from '../../components/common/iconBox/iconBox';
import {useAppSelector} from '../../helpers/reduxHelpers';
import {SettingsDevelopersPagesEnum, SettingsDevelopersUrl} from '../../models/url/settingsUrlModel';
import {getIsSettingsLocalApplicationLoading} from '../../store/panels/settings/settingsPanelSelectors';
import {SettingsHomeArticle} from '../home/settingsHomeArticle';
import {
  computeInfoBoxTypeFromUrl,
  settingsDevelopersDescriptionRenderMap,
  settingsDevelopersTitleRenderMap,
} from './helpers/developersInfoBoxHelpers';

/*
 * Props.
 */

interface SettingsDevelopersInfoBoxProps {
  url: SettingsDevelopersUrl;
  isInfoBoxExpanded: boolean;
  infoBoxKey?: string;
}

/*
 * Style.
 */

const StyledIconBoxWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  flex: 0 0 248px;
  gap: 16px;
`;

const StyledIconBox = styled(IconBox)`
  padding: 17px;
`;

const StyledTitleDiv = styled.div`
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.semibold};
  padding: 0 16px 10px 0;
  line-height: 20px;
`;

const StyledBodyDiv = styled.div`
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};
  line-height: 22px;
`;

const StyledContentDiv = styled.div`
  display: flex;
  flex-flow: column;
`;

const StyledArticleWrapperDiv = styled.div`
  border: 1px solid ${(p) => p.theme.alphas.gray20};
  border-radius: 16px;
  padding: 9px 9px 0 9px;
  margin-bottom: -4px;
`;

const StyledSettingsHomeArticle = styled(SettingsHomeArticle)`
  margin-bottom: -3px;
`;

/*
 * Component.
 */

export const SettingsDevelopersInfoBox: FC<SettingsDevelopersInfoBoxProps> = (props) => {
  const {url, isInfoBoxExpanded, infoBoxKey} = props;
  const infoBoxType = computeInfoBoxTypeFromUrl(url);
  const theme = useUserTheme();
  const isApplicationLoading = useAppSelector(getIsSettingsLocalApplicationLoading);

  if (!infoBoxKey || !infoBoxType || !isInfoBoxExpanded) {
    return null;
  }
  // If we are in a specific application and it is currently loading.
  if (
    isApplicationLoading &&
    url.subPage !== SettingsDevelopersPagesEnum.APPLICATIONS &&
    url.subPage !== SettingsDevelopersPagesEnum.TOKENS
  ) {
    return null;
  }
  return (
    <StyledIconBoxWrapperDiv>
      <StyledIconBox iconName="info" iconSize={20} backgroundColor={theme.greys.shade20} borderRadius={16}>
        <StyledContentDiv>
          <StyledTitleDiv>{settingsDevelopersTitleRenderMap[infoBoxType]}</StyledTitleDiv>
          <StyledBodyDiv>{settingsDevelopersDescriptionRenderMap[infoBoxType]}</StyledBodyDiv>
        </StyledContentDiv>
      </StyledIconBox>
      {maybeRenderArticles(url)}
    </StyledIconBoxWrapperDiv>
  );
};

/*
 * Helpers.
 */

function maybeRenderArticles(url: SettingsDevelopersUrl) {
  if (
    url.subPage === SettingsDevelopersPagesEnum.APPLICATIONS ||
    url.subPage === SettingsDevelopersPagesEnum.TOKENS
  ) {
    return (
      <>
        <StyledArticleWrapperDiv>
          <StyledSettingsHomeArticle
            title={<Trans id="settings-developers-article-basics-title">Front basics</Trans>}
            description={
              <Trans id="settings-developers-article-basics-description">
                Learn more about Front&apos;s benefits, key concepts, and basic configuration.
              </Trans>
            }
            link="https://help.front.com/category/getting-started"
            icon="helpFilledSmall"
            articleColor={PaletteColorsEnum.BLUE}
            descriptionLines={3}
          />
        </StyledArticleWrapperDiv>
        <StyledArticleWrapperDiv>
          <StyledSettingsHomeArticle
            title={<Trans id="settings-developers-article-dev-docs-title">Developer docs</Trans>}
            description={
              <Trans id="settings-developers-article-dev-docs-description">
                Guides, sample code, reference docs and more to help you build your app.
              </Trans>
            }
            link="https://dev.frontapp.com/docs"
            icon="developers"
            articleColor={PaletteColorsEnum.PURPLE}
            descriptionLines={3}
          />
        </StyledArticleWrapperDiv>
      </>
    );
  }
  return null;
}
